import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { PendingQuestion } from '../queries';
import type { QuestionAnswersDraft } from '../pendingQuestions';
import { RUTERO_CONFIRM_ANSWERS_MAX } from '@bandido/shared/types';

import {
  answersOmittedFromConfirm,
  isQuestionAnswered,
  isQuestionMandatory,
  questionKey,
  questionOptions,
  questionPassengerLabel,
} from '../pendingQuestions';

interface Props {
  /** Las preguntas de la RESERVA: la misma lista que bloquea el CTA. */
  questions: PendingQuestion[];
  answers: QuestionAnswersDraft;
  /**
   * Recibe la IDENTIDAD de la pregunta (`questionKey`), no su código: con
   * preguntas por pasajero hay varias con el mismo código y usarlo de clave
   * las haría compartir el valor.
   */
  onAnswer: (key: string, value: string) => void;
  /**
   * Los pasajeros de la reserva, para rotular cada copia de una pregunta por
   * pasajero. Ausentes: la copia se rotula con su número.
   */
  passengers?: { firstName?: string; lastName?: string }[];
}

/**
 * Lo que el OPERADOR pregunta para poder emitir, en su propia sección del
 * momento Reservado.
 *
 * El `prompt` se muestra TEXTUAL y completo, en su caja: es el texto que el
 * operador escribió "para mostrárselo al usuario" (contrato), y el asesor se
 * lo lee al pasajero tal cual. Por eso no va como label de MUI —que flota,
 * encoge y recorta con puntos suspensivos— sino como el título de la caja,
 * con el campo de respuesta debajo.
 *
 * Ninguna respuesta se completa sola, ni siquiera "sugerida": una respuesta
 * inventada CONFIRMA la reserva con un dato incorrecto (contrato del hub). La
 * pregunta de documento no se dibuja aparte porque el hub ya la contesta con
 * los pasajeros del hold: acá está lo que él publicó como pendiente y nada más.
 *
 * El CONTROL sale de `dataType`, el vocabulario cerrado que normaliza el hub
 * — acá no se interpreta nada del proveedor: una elección con lista es un
 * select que muestra `label` y responde `value` (el operador rechaza
 * cualquier otra cosa), una fecha es un datepicker que responde `YYYY-MM-DD`,
 * y todo lo demás —incluido `other`, que suma la declaración cruda como
 * pista— es texto libre.
 */
export function PendingQuestionsSection({
  questions,
  answers,
  onAnswer,
  passengers,
}: Props) {
  const { t } = useTranslation();
  if (questions.length === 0) return null;
  // Cuántas de las respondidas no entran en el confirm. Se dice: recortar en
  // silencio dejaría al asesor creyendo que mandó algo que no mandó.
  const omitted = answersOmittedFromConfirm(questions, answers);

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box>
        <Typography variant="subtitle2">
          {t('travelServices.booking.questionsTitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('travelServices.booking.questionsNotice')}
        </Typography>
        {omitted > 0 && (
          <Typography variant="caption" color="warning.main" display="block">
            {t('travelServices.booking.questionsOmitted', {
              count: omitted,
              max: RUTERO_CONFIRM_ANSWERS_MAX,
            })}
          </Typography>
        )}
      </Box>
      {questions.map((question, index) => {
        const mandatory = isQuestionMandatory(question);
        const missing = mandatory && !isQuestionAnswered(question, answers);
        // El campo se ancla al prompt para lectores de pantalla: su label es
        // genérico ("Respuesta") y la pregunta es lo que hay que oír.
        const promptId = `booking-question-${index}`;
        const formatId = `booking-question-format-${index}`;
        // El control sale del vocabulario del hub, no de adivinarle al texto.
        const options = questionOptions(question);
        const isDate = question.dataType === 'date';
        const isNumber = question.dataType === 'number';
        // Un sí/no se ELIGE. El literal que viaja es el canónico del contrato;
        // si el operador espera otra forma, traducirla es del hub — es la
        // misma regla que el contrato ya declara para `date` ("mandarla como
        // YYYY-MM-DD: si el operador espera otra forma, traducirla es
        // problema del hub, no del consumidor").
        const booleanChoices = question.dataType === 'boolean'
          ? [
            { value: 'true', label: t('travelServices.booking.questionYes') },
            { value: 'false', label: t('travelServices.booking.questionNo') },
          ]
          : [];
        const choices = options.length > 0
          ? options
          : booleanChoices;
        const hint = question.dataType === 'other' && Boolean(question.dataTypeRaw);
        const key = questionKey(question);
        const passenger = questionPassengerLabel(question, passengers);
        return (
          <Box
            key={key}
            sx={{
              border: 1,
              // La obligatoria sin responder se ve como lo que es: lo que
              // falta para confirmar, no un campo más.
              borderColor: missing ? 'warning.main' : 'divider',
              borderRadius: 1,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {passenger && (
              // La copia se ata a SU pasajero: "pregunta 1 / pregunta 2" no
              // le dice al asesor de quién es cada dato.
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {passenger.name
                  ? t('travelServices.booking.questionForPassengerNamed', {
                    number: passenger.number,
                    name: passenger.name,
                  })
                  : t('travelServices.booking.questionForPassenger', {
                    number: passenger.number,
                  })}
              </Typography>
            )}
            <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
              <Typography
                id={promptId}
                variant="body2"
                fontStyle="italic"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {question.prompt}
              </Typography>
              {mandatory && (
                <Box
                  component="span"
                  sx={{
                    bgcolor: 'warning.light',
                    color: 'warning.contrastText',
                    px: 0.75,
                    borderRadius: 0.75,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    lineHeight: 1.6,
                  }}
                >
                  {t('travelServices.booking.questionMandatory')}
                </Box>
              )}
            </Box>
            {question.dataType === 'other' && question.dataTypeRaw && (
              // La declaración cruda del operador, como pista. El hub la
              // garantiza corta y no hay que interpretarla.
              <Typography
                id={formatId}
                variant="caption"
                color="text.secondary"
                sx={{ overflowWrap: 'anywhere' }}
              >
                {t('travelServices.booking.questionFormat', {
                  format: question.dataTypeRaw,
                })}
              </Typography>
            )}
            <TextField
              size="small"
              fullWidth
              // La elección cerrada se elige, no se tipea: así la respuesta es
              // siempre un `value` que el operador acepta.
              select={choices.length > 0}
              multiline={choices.length === 0 && !isDate && !isNumber}
              type={isDate ? 'date' : isNumber ? 'number' : undefined}
              required={mandatory}
              label={t('travelServices.booking.questionAnswer')}
              value={answers[key] ?? ''}
              onChange={(event) => onAnswer(key, event.target.value)}
              InputLabelProps={isDate || isNumber ? { shrink: true } : undefined}
              inputProps={{
                // Sin `maxLength`: el contrato no acota la respuesta, y un
                // tope nuestro cortaría en silencio lo que el operador acepta.
                // La pregunta y —si el operador la declaró— la pista de
                // formato: las dos se leen antes del campo.
                'aria-describedby': hint ? `${promptId} ${formatId}` : promptId,
              }}
            >
              {choices.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        );
      })}
    </Box>
  );
}
